<template>
  <v-card variant="flat" class="platform-email__panel">
    <div class="platform-email__panel-header">
      <div>
        <h2>{{ $t("admin.platformEmail.smtp.title") }}</h2>
        <p>{{ $t("admin.platformEmail.smtp.subtitle") }}</p>
      </div>
      <v-chip :color="testStatusColor">
        {{ testStatusLabel }}
      </v-chip>
    </div>

    <v-skeleton-loader v-if="loading" type="article, actions" />
    <v-form v-else class="platform-email__form" @submit.prevent="saveSettings">
      <v-alert v-if="errorMessage" type="error">{{ errorMessage }}</v-alert>
      <v-alert v-if="successMessage" type="success">{{ successMessage }}</v-alert>

      <div class="platform-email__status-row">
        <div>
          <strong>{{ $t("admin.platformEmail.smtp.enabled") }}</strong>
          <p>{{ $t("admin.platformEmail.smtp.enabledHint") }}</p>
        </div>
        <v-switch v-model="form.enabled" color="success" hide-details inset />
      </div>

      <v-row>
        <v-col cols="12" md="8">
          <v-text-field v-model="form.host" :label="$t('admin.platformEmail.smtp.fields.host')" required />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field v-model.number="form.port" :label="$t('admin.platformEmail.smtp.fields.port')" type="number" required />
        </v-col>
        <v-col cols="12" md="6">
          <v-select v-model="form.authType" :items="authTypeItems" :label="$t('admin.platformEmail.smtp.fields.authType')" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="form.username" :label="$t('admin.platformEmail.smtp.fields.username')" :disabled="form.authType === 'none'" autocomplete="username" />
        </v-col>
        <v-col v-if="form.authType === 'password'" cols="12">
          <v-text-field
            v-model="form.password"
            :label="$t('admin.platformEmail.smtp.fields.password')"
            :hint="settings?.hasPassword ? $t('admin.platformEmail.smtp.passwordStored') : $t('admin.platformEmail.smtp.passwordMissing')"
            persistent-hint
            type="password"
            autocomplete="new-password"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-checkbox v-model="form.secure" :label="$t('admin.platformEmail.smtp.fields.secure')" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-checkbox v-model="form.requireTLS" :label="$t('admin.platformEmail.smtp.fields.requireTls')" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="form.fromEmail" :label="$t('admin.platformEmail.smtp.fields.fromEmail')" type="email" required />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="form.fromName" :label="$t('admin.platformEmail.smtp.fields.fromName')" />
        </v-col>
        <v-col cols="12">
          <v-text-field v-model="form.replyToEmail" :label="$t('admin.platformEmail.smtp.fields.replyToEmail')" type="email" />
          <!-- Internal MiriDar recipient for lifecycle warnings and deletion
               reports; not a tenant-facing address. -->
          <v-text-field
            v-model="form.adminNotificationEmail"
            :label="$t('admin.platformEmail.fields.adminNotificationEmail')"
            :hint="$t('admin.platformEmail.fields.adminNotificationEmailHint')"
            persistent-hint
            type="email"
          />
        </v-col>
      </v-row>

      <div class="platform-email__actions">
        <v-btn color="primary" type="submit" :loading="saving" prepend-icon="mdi-content-save-outline">
          {{ $t("admin.platformEmail.actions.saveSettings") }}
        </v-btn>
        <v-btn variant="tonal" :disabled="!hasSavedSettings" :loading="testing" prepend-icon="mdi-lan-connect" @click="testConnection">
          {{ $t("admin.platformEmail.actions.testConnection") }}
        </v-btn>
      </div>

      <div class="platform-email__test-box">
        <div>
          <strong>{{ $t("admin.platformEmail.smtp.testEmailTitle") }}</strong>
          <p>{{ $t("admin.platformEmail.smtp.lastTested", { date: lastTestedLabel }) }}</p>
        </div>
        <div class="platform-email__test-controls">
          <v-text-field v-model="testRecipient" :label="$t('admin.platformEmail.smtp.fields.testRecipient')" type="email" hide-details />
          <v-btn variant="tonal" :disabled="!hasSavedSettings || !testRecipient" :loading="sendingTest" prepend-icon="mdi-email-fast-outline" @click="sendTestEmail">
            {{ $t("admin.platformEmail.actions.sendTest") }}
          </v-btn>
        </div>
        <v-alert v-if="settings?.lastTestError" type="warning" density="compact">
          {{ settings.lastTestError }}
        </v-alert>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import platformEmailSmtpLogic from "./platformEmailSmtpLogic.mjs";

export default {
  name: "PlatformEmailSmtpCard",
  ...platformEmailSmtpLogic,
};
</script>

<style scoped src="./platformEmail.css"></style>
